import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  title: "",
  client: "",
  due: "Due Today",
  priority: "Medium",
  status: "Pending",
};

export default function TaskFormDialog({
  open,
  onOpenChange,
  onSubmit,
  initial,
  clientOptions = [],
}) {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) setForm({ ...EMPTY, ...(initial || {}) });
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const valid = form.title.trim().length > 0;

  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit(form);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{initial ? "Edit Task" : "Create Task"}</DialogTitle>
          <DialogDescription>
            Demo task — saved in local state only.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="t-title" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Title</Label>
            <Input id="t-title" data-testid="task-form-title" value={form.title} onChange={set("title")} placeholder="Send invoice to..." className="h-10 rounded-lg" required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="t-client" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Client</Label>
              <Select value={form.client} onValueChange={set("client")}>
                <SelectTrigger data-testid="task-form-client" className="h-10 rounded-lg">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clientOptions.length === 0 && (
                    <SelectItem value="none" disabled>No clients</SelectItem>
                  )}
                  {clientOptions.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="t-due" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Due</Label>
              <Select value={form.due} onValueChange={set("due")}>
                <SelectTrigger data-testid="task-form-due" className="h-10 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Due Today">Due Today</SelectItem>
                  <SelectItem value="Due Tomorrow">Due Tomorrow</SelectItem>
                  <SelectItem value="This Week">This Week</SelectItem>
                  <SelectItem value="No due date">No due date</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="t-priority" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Priority</Label>
              <Select value={form.priority} onValueChange={set("priority")}>
                <SelectTrigger data-testid="task-form-priority" className="h-10 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="t-status" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="task-form-status" className="h-10 rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="task-form-cancel" className="rounded-lg">
              Cancel
            </Button>
            <Button type="submit" disabled={!valid} data-testid="task-form-submit" className="bg-primary hover:bg-primary/90 rounded-lg">
              {initial ? "Save Changes" : "Create Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
